// AdminProfileSummary — the "YOU" card in the admin Hem right rail (#358), the
// admin counterpart to EmployerProfileSummary.
//
// An admin has no unit and no organisation, so the facts an employer's card
// shows do not exist here. Rather than pad it out, this shows what is true —
// who you are signed in as — plus the destinations, which is the other thing a
// landing page owes you. That is why the admin Hem is worth having at all.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Mail, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const DESTINATIONS = [
    { icon: Building2, label: 'Organisationer', path: '/dashboard/organisations' },
    { icon: Users, label: 'Användare', path: '/dashboard/users' },
    { icon: Mail, label: 'Kontaktförfrågningar', path: '/dashboard/contacts' },
];

const AdminProfileSummary: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Min profil';
    const initials =
        ((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() ||
        (user?.email?.[0] ?? '?').toUpperCase();

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-5`}>
            <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12 ring-2 ring-white shadow-sm">
                    <AvatarImage src={resolveMediaUrl(user?.picture_url)} alt={name} />
                    <AvatarFallback className="bg-[#A4C3B2]/30 text-[#1A4D4F] font-semibold">
                        {initials}
                    </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    <p className="text-sm text-gray-500">Administratör</p>
                </div>
            </div>

            <div className="mt-4 space-y-1 border-t border-gray-100 pt-3">
                {DESTINATIONS.map(({ icon: Icon, label, path }) => (
                    <button
                        key={path}
                        onClick={() => navigate(path)}
                        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                    >
                        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{label}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-300 ml-auto group-hover:text-[#1A4D4F] transition-colors" />
                    </button>
                ))}
            </div>

            <button
                onClick={() => navigate('/account-settings')}
                className="mt-3 w-full text-sm font-medium text-[#1A4D4F] hover:underline"
            >
                Kontoinställningar →
            </button>
        </div>
    );
};

export default AdminProfileSummary;
